/**
 * Incognito/Private Mode Detection
 * Detects if user is in private browsing mode where data won't persist
 */

/**
 * Detect if browser is in incognito/private mode
 * Returns true if incognito is detected, false if normal mode, null if uncertain
 */
export async function isIncognitoMode(): Promise<boolean | null> {
  // Method 1: Check if localStorage persists (most reliable)
  try {
    const testKey = '__incognito_test__';
    localStorage.setItem(testKey, '1');

    // In incognito, localStorage exists but may be limited
    // Check if we can actually retrieve it
    const canPersist = localStorage.getItem(testKey) === '1';
    localStorage.removeItem(testKey);

    if (!canPersist) {
      return true; // Incognito detected
    }
  } catch (e) {
    // localStorage throws in some incognito modes
    return true;
  }

  // Method 2: Estimate storage quota (incognito has much lower quota)
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      const quota = estimate.quota || 0;

      // Normal browsers: 10GB+ quota
      // Incognito mode: typically 10MB or less
      if (quota < 120000000) { // Less than 120MB
        return true; // Likely incognito
      }
    } catch (e) {
      // Estimate failed, can't determine
    }
  }

  // Method 3: Check for Safari's private mode
  try {
    // @ts-ignore - Safari specific
    if ('safari' in window && window.safari?.pushNotification) {
      return false; // Normal Safari
    }
  } catch (e) {
    return true; // Private Safari
  }

  // Couldn't definitively determine - assume normal mode
  return false;
}

/**
 * Show warning if incognito mode is detected
 */
export async function warnIfIncognito(): Promise<void> {
  const incognito = await isIncognitoMode();

  if (incognito === true) {
    console.warn('🔒 Incognito/Private mode detected');
    console.warn('⚠️ Your scent reports and comments may be lost when you close this tab');
    console.warn('💡 For permanent storage, please use normal browsing mode or log in');
  }
}

/**
 * Get incognito status with caching
 */
let cachedIncognitoStatus: boolean | null = null;

export async function getIncognitoStatus(): Promise<boolean> {
  if (cachedIncognitoStatus !== null) {
    return cachedIncognitoStatus;
  }

  cachedIncognitoStatus = await isIncognitoMode() ?? false;
  return cachedIncognitoStatus;
}
